import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RotateCcw, Check, AlertCircle, Loader2, Settings, Upload as UploadIcon } from 'lucide-react';
import { getCurrentUser } from '../services/auth';

const FN = '/.netlify/functions/giphy-proxy';
async function giphy(action, params = {}) {
  const res = await fetch(FN, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...params }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export default function GiphySync() {
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map((r) => r.trim());
  const isAllowed = roles.includes('Admin') || roles.includes('Marketing');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [channel, setChannel] = useState('');
  const [uploadKey, setUploadKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [job, setJob] = useState(null);
  const [uploads, setUploads] = useState([]);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await giphy('status');
      setStatus(s);
      setChannel((c) => c || s.channel || '');
      setJob(s.lastSync || null);
      if (!s.connected) setShowSetup(true);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadUploads = useCallback(async () => {
    try { const d = await giphy('upload-status'); setUploads(d.uploads || []); } catch { /* noop */ }
  }, []);

  useEffect(() => { if (isAllowed) { loadStatus(); loadUploads(); } }, [isAllowed, loadStatus, loadUploads]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const saveConfig = async () => {
    setSaving(true); setErr(null);
    try {
      const params = {};
      if (apiKey.trim()) params.apiKey = apiKey.trim();
      if (channel.trim()) params.channel = channel.trim();
      if (uploadKey.trim()) params.uploadKey = uploadKey.trim();
      await giphy('set-config', params);
      setApiKey(''); setUploadKey('');
      await loadStatus();
      setShowSetup(false);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const startSync = async () => {
    setSyncing(true); setErr(null);
    try {
      const { jobId } = await giphy('sync-start');
      pollRef.current = setInterval(async () => {
        try {
          const { job: j } = await giphy('sync-status', { jobId });
          setJob(j);
          if (j && (j.status === 'done' || j.status === 'failed')) {
            clearInterval(pollRef.current); pollRef.current = null;
            setSyncing(false);
            loadStatus();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { setErr(e.message); setSyncing(false); }
  };

  if (!isAllowed) {
    return <div style={{ padding: 24 }}><h1 style={{ color: 'var(--navy)' }}>Giphy Sync</h1><p style={{ color: 'var(--gray-500)' }}>You need Admin or Marketing access.</p></div>;
  }

  const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <h1 style={{ margin: 0, color: 'var(--navy)', fontWeight: 700 }}>Giphy</h1>
        <button onClick={() => { loadStatus(); loadUploads(); }} title="Refresh" style={btnGhost}><RotateCcw size={14} /></button>
      </div>
      <p style={{ color: 'var(--gray-500)', marginTop: 0, fontSize: 13 }}>
        Sync your Giphy channel's GIFs into the Media Library, and (once an Upload Production Key is added) send new GIFs back to Giphy.
      </p>

      {err ? <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</div> : null}
      {loading ? <div style={{ color: 'var(--gray-500)' }}><Loader2 size={14} className="spin" style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} /> Loading…</div> : null}

      {status ? (
        <>
          {/* Connection card */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Badge ok={status.connected} label={status.connected ? 'Connected' : 'Not connected'} />
                {status.channel ? <span style={{ fontSize: 13 }}>Channel: <strong>@{status.channel}</strong></span> : null}
                <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>· {status.syncedCount} GIFs synced in</span>
                <Badge ok={status.hasUploadKey} label={status.hasUploadKey ? 'Upload key set' : 'No upload key (pull only)'} />
              </div>
              <button onClick={() => setShowSetup((v) => !v)} style={btnGhost}><Settings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Setup</button>
            </div>

            {showSetup ? (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--gray-200)', paddingTop: 14, display: 'grid', gap: 10 }}>
                <Field label="Giphy API key" hint="From developers.giphy.com → your app. Beta key is enough to pull.">
                  <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={status.connected ? '•••••••• (leave blank to keep)' : 'paste API key'} style={input} />
                </Field>
                <Field label="Channel username" hint="Your Giphy @handle, without the @.">
                  <input type="text" value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="thecrabplace" style={input} />
                </Field>
                <Field label="Upload Production Key" hint="Required only to SEND GIFs to Giphy. Request it in the Giphy dashboard (beta keys can't post to a channel).">
                  <input type="text" value={uploadKey} onChange={(e) => setUploadKey(e.target.value)} placeholder={status.hasUploadKey ? '•••••••• (leave blank to keep)' : 'paste upload production key'} style={input} />
                </Field>
                <div><button onClick={saveConfig} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button></div>
              </div>
            ) : null}
          </div>

          {/* Sync-in card */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Sync from Giphy</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Pulls every GIF on @{status.channel || '…'} into the Media Library. Re-running only imports new ones.</div>
              </div>
              <button onClick={startSync} disabled={syncing || !status.connected} style={btnPrimary}>
                {syncing ? <><Loader2 size={14} className="spin" style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />Syncing…</> : 'Sync from Giphy'}
              </button>
            </div>
            {job ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ height: 8, background: 'var(--gray-200)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: job.status === 'failed' ? '#c33' : 'var(--orange)', transition: 'width .3s' }} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                  [{job.status}] {job.processed}/{job.total} · imported {job.imported} · skipped {job.skipped}{job.failed ? ` · failed ${job.failed}` : ''}
                  {job.error ? <span style={{ color: '#c33' }}> · {job.error}</span> : null}
                </div>
              </div>
            ) : null}
          </div>

          {/* Push log */}
          <div style={card}>
            <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 4 }}><UploadIcon size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Sent to Giphy</div>
            {!status.hasUploadKey ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                Add an <strong>Upload Production Key</strong> in Setup to enable sending GIFs to your channel. Then use <strong>“Send to Giphy”</strong> on any GIF in the Media Library. (Request the key at developers.giphy.com → your app → Upgrade to Production.)
              </div>
            ) : uploads.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No uploads yet. Use “Send to Giphy” on a GIF in the Media Library.</div>
            ) : (
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--gray-500)' }}><th style={th}>GIF</th><th style={th}>Status</th><th style={th}>Link</th></tr></thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                      <td style={td}>{u.suggested_name || u.asset_id}</td>
                      <td style={td}>{u.status}{u.error ? <span style={{ color: '#c33' }}> · {u.error}</span> : ''}</td>
                      <td style={td}>{u.giphy_url ? <a href={u.giphy_url} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)' }}>view</a> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Badge({ ok, label }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: ok ? '#e6f4ea' : '#fdeaea', color: ok ? '#1a7f37' : '#c33' }}>{ok ? <Check size={11} /> : <AlertCircle size={11} />}{label}</span>;
}
function Field({ label, hint, children }) {
  return <label style={{ display: 'block' }}><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)' }}>{label}</div>{hint ? <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{hint}</div> : null}{children}</label>;
}

const card = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginTop: 14 };
const input = { width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, boxSizing: 'border-box', fontFamily: 'inherit' };
const btnPrimary = { padding: '8px 16px', fontSize: 13, fontWeight: 600, background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnGhost = { padding: '5px 10px', fontSize: 12, background: '#fff', color: '#1d1d1f', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer' };
const errBox = { color: '#c33', padding: 10, marginTop: 10, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6, fontSize: 13 };
const th = { padding: '6px 8px', fontWeight: 600 };
const td = { padding: '6px 8px' };
